import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useIsRelayPlatformAdmin } from "@/hooks/useIsRelayPlatformAdmin";
import { AuthInitializationRecovery } from "@/components/AuthInitializationRecovery";

/**
 * Gates a route on (a) an authenticated session and (b) the
 * `relay_platform_admin` / `super_admin` JWT role.
 *
 * Lifecycle mirrors `ProtectedRoute`:
 *   - Tiny loading shell while session restore is in flight.
 *   - Redirect to `/login` if unauthenticated, preserving the attempted path.
 *   - Redirect to `/dashboard` if authenticated but missing the role
 *     (Relay doesn't have a dedicated /403 page; bouncing to dashboard
 *     keeps the UX simple and the user inside the app).
 *
 * RLS enforces the real answer on the server via the
 * `is_relay_platform_admin_from_jwt()` predicate; this guard is for UX,
 * not security.
 */
export function PlatformAdminRoute({ children }: { children: ReactNode }) {
  const { session, isLoading, initializationError } = useAuth();
  const { isPlatformAdmin } = useIsRelayPlatformAdmin();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <p className="text-sm text-slate-500">Loading…</p>
      </div>
    );
  }

  if (initializationError) {
    return <AuthInitializationRecovery />;
  }

  if (!session) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isPlatformAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
